import { useState, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Card } from '@/components/ui/card';
import { Slider } from '@/components/ui/slider';
import { TIERS } from '@/data/rewardsData';
import { Calculator, Sparkles } from 'lucide-react';

export const PointsCalculator = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-80px' });
  const [annualSpend, setAnnualSpend] = useState([150000]);

  const spend = annualSpend[0];

  // Determine tier
  const currentTier = TIERS.reduce((acc, tier) => {
    if (spend >= tier.spendRange[0]) return tier;
    return acc;
  }, TIERS[0]);

  // Calculate points earned
  const gpEarned = Math.floor((spend / 100) * currentTier.earnRate);
  const gpValue = Math.floor(gpEarned / 1000) * 500;
  const discountSavings = Math.floor(spend * (parseInt(currentTier.discount) / 100));

  const formatCurrency = (val) => {
    if (val >= 100000) return `₹${(val / 100000).toFixed(1)}L`;
    if (val >= 1000) return `₹${(val / 1000).toFixed(0)}K`;
    return `₹${val}`;
  };

  return (
    <section ref={ref} className="relative py-24 lg:py-32 overflow-hidden">
      <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, transparent 0%, hsl(var(--card) / 0.3) 50%, transparent 100%)' }} />

      <div className="relative max-w-4xl mx-auto px-6 lg:px-8">
        {/* Royal ornamental divider */}
        <div className="flex items-center gap-3 justify-center mb-10">
          <div className="h-px w-16 bg-gold/20" />
          <svg width="16" height="16" viewBox="0 0 20 20" className="text-gold/40">
            <path d="M10 0L12 8L20 10L12 12L10 20L8 12L0 10L8 8Z" fill="currentColor" />
          </svg>
          <div className="h-px w-16 bg-gold/20" />
        </div>

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7 }}
          className="text-center mb-12"
        >
          <span className="font-body text-xs tracking-[0.3em] uppercase text-gold mb-4 block">Calculator</span>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-foreground font-medium leading-tight">
            See Your <span className="text-shimmer-gold italic">Rewards</span>
          </h2>
          <p className="font-body text-base text-muted-foreground mt-4 max-w-lg mx-auto leading-relaxed">
            Slide to see how much you could earn based on your annual spend.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7, delay: 0.2 }}
        >
          <Card className="bg-gradient-card border-border/50 rounded-2xl p-8 lg:p-10">
            {/* Slider */}
            <div className="mb-10">
              <div className="flex items-center justify-between mb-4">
                <span className="font-body text-sm text-muted-foreground">Annual Spend</span>
                <span className="font-display text-2xl text-gold">{formatCurrency(spend)}</span>
              </div>
              <Slider
                value={annualSpend}
                onValueChange={setAnnualSpend}
                min={0}
                max={1500000}
                step={10000}
                className="[&_[role=slider]]:bg-gold [&_[role=slider]]:border-gold [&_[role=slider]]:shadow-gold [&_[role=slider]]:w-5 [&_[role=slider]]:h-5 [&_[data-orientation=horizontal]>[data-orientation=horizontal]]:bg-gold/30 [&_[role=slider]]:focus-visible:ring-gold/30"
              />
              <div className="flex justify-between mt-2">
                <span className="font-body text-[10px] text-muted-foreground">₹0</span>
                <span className="font-body text-[10px] text-muted-foreground">₹15L</span>
              </div>
            </div>

            {/* Current Tier Display */}
            <div className="flex items-center gap-3 mb-8 pb-6 border-b border-border/30">
              <span className="text-3xl">{currentTier.icon}</span>
              <div>
                <p className="font-body text-xs text-muted-foreground uppercase tracking-wider">Your Tier</p>
                <p className="font-display text-xl text-foreground">{currentTier.name}</p>
              </div>
              <div className="ml-auto text-right">
                <p className="font-body text-xs text-muted-foreground">Earn Rate</p>
                <p className="font-body text-lg text-gold font-medium">{currentTier.earn}</p>
              </div>
            </div>

            {/* Results Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
              <div className="text-center p-4 rounded-xl bg-background/50">
                <Sparkles className="w-5 h-5 text-gold mx-auto mb-2" />
                <p className="font-display text-2xl text-gold">{gpEarned.toLocaleString()}</p>
                <p className="font-body text-xs text-muted-foreground mt-1">Glow Points Earned</p>
              </div>
              <div className="text-center p-4 rounded-xl bg-background/50">
                <Calculator className="w-5 h-5 text-gold mx-auto mb-2" />
                <p className="font-display text-2xl text-foreground">₹{gpValue.toLocaleString()}</p>
                <p className="font-body text-xs text-muted-foreground mt-1">GP Redemption Value</p>
              </div>
              <div className="text-center p-4 rounded-xl bg-background/50">
                <Calculator className="w-5 h-5 text-gold mx-auto mb-2" />
                <p className="font-display text-2xl text-foreground">₹{discountSavings.toLocaleString()}</p>
                <p className="font-body text-xs text-muted-foreground mt-1">Tier Discount Savings</p>
              </div>
            </div>

            {/* Total Savings */}
            <div className="mt-6 p-4 rounded-xl border border-gold/15 bg-gold/5 text-center">
              <p className="font-body text-xs text-muted-foreground mb-1">Total Annual Benefit</p>
              <p className="font-display text-3xl text-gold">₹{(gpValue + discountSavings).toLocaleString()}</p>
            </div>
          </Card>
        </motion.div>
      </div>
    </section>
  );
};
